import { useTranslation } from '@thoughtspot/i18n';
import { VisualPropComponentTranslationKeys, } from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { sdkToTSTranslationKeyMap } from '../../utils/form-builder.util';
import { CheckboxFormComponent } from './checkbox-form.component';
import { ColorPickerFormComponent } from './color-picker-form.component';
import { DropDownFormComponent } from './drop-down-form.component';
import { NativeChartConfigComponent } from './native-chart-config-form-component';
import { NumberInputFormComponent } from './number-input-form.component';
import { RadioFormComponent } from './radio-form.component';
import { WrapWithLayout, WrapWithOrWithoutTabs, } from './section-form.component';
import { TextInputFormComponent } from './text-input-form.component';
import { ToggleFormComponent } from './toggle-form.component';
const PropElementComponent = ({ propElement, visualProps, elementKey, onChange, nativeConfigItemsForCustomChart, }) => {
    const isFirstSection = elementKey.split('.').length === 1;
    const { t } = useTranslation();
    let propElementLabelWithTranslation = '';
    if (propElement.type !== 'customsort') {
        propElementLabelWithTranslation = propElement?.label;
        if (Object.values(VisualPropComponentTranslationKeys).includes(propElement?.labelTranslation)) {
            propElementLabelWithTranslation = t(sdkToTSTranslationKeyMap[propElement?.labelTranslation]);
        }
        else if (typeof propElement?.labelTranslation === 'string') {
            propElementLabelWithTranslation = t(propElement?.labelTranslation);
        }
    }
    switch (propElement.type) {
        case 'section':
            return (React.createElement(WrapWithLayout, { elementKey: elementKey, propElement: propElement, layoutType: isFirstSection
                    ? propElement.layoutType ?? 'accordion'
                    : 'none' }, propElement.children.map(childSection => (React.createElement(PropElementComponent, { key: `${elementKey}.${childSection.key}`, propElement: childSection, elementKey: `${elementKey}.${childSection.key}`, onChange: onChange, visualProps: visualProps, nativeConfigItemsForCustomChart: nativeConfigItemsForCustomChart })))));
        case 'text':
            return (React.createElement(TextInputFormComponent, { defaultValue: propElement.defaultValue, label: propElementLabelWithTranslation, elementKey: elementKey, onChange: onChange, password: propElement.password, value: _.get(visualProps, elementKey), inputValidation: propElement.inputValidation, isDisabled: propElement.disabled }));
        case 'number':
            return (React.createElement(NumberInputFormComponent, { defaultValue: propElement.defaultValue, label: propElementLabelWithTranslation, elementKey: elementKey, onChange: onChange, value: _.get(visualProps, elementKey), inputValidation: propElement.inputValidation, isDisabled: propElement.disabled }));
        case 'toggle':
            return (React.createElement(ToggleFormComponent, { defaultValue: propElement.defaultValue, label: propElementLabelWithTranslation, elementKey: elementKey, onChange: onChange, selectedValue: _.get(visualProps, elementKey), isDisabled: propElement.disabled }));
        case 'checkbox':
            return (React.createElement(CheckboxFormComponent, { label: propElementLabelWithTranslation, defaultValue: propElement.defaultValue, elementKey: elementKey, onChange: onChange, selectedValue: _.get(visualProps, elementKey), isDisabled: propElement.disabled }));
        case 'radio':
            return (React.createElement(RadioFormComponent, { label: propElementLabelWithTranslation, defaultValue: propElement.defaultValue, elementKey: elementKey, onChange: onChange, selectedValue: _.get(visualProps, elementKey), values: propElement.values, isDisabled: propElement.disabled }));
        case 'dropdown':
            return (React.createElement(DropDownFormComponent, { label: propElementLabelWithTranslation, defaultValue: propElement.defaultValue, elementKey: elementKey, onChange: onChange, selectedValue: _.get(visualProps, elementKey), values: propElement.values, isDisabled: propElement.disabled }));
        case 'colorpicker':
            return (React.createElement(ColorPickerFormComponent, { onChange: onChange, elementKey: elementKey, defaultValue: _.get(visualProps, elementKey) ||
                    propElement.defaultValue, label: propElementLabelWithTranslation, selectorType: propElement.selectorType }));
        case 'tooltipconfig':
            return (React.createElement(NativeChartConfigComponent, { type: propElement.type, componentItem: nativeConfigItemsForCustomChart?.tooltipconfig, onChangeHandler: onChange, elementKey: elementKey, label: propElement.label, defaultValue: _.get(visualProps, elementKey) ||
                    propElement.defaultValue }));
        case 'customsort': {
            const customSortElement = propElement;
            return (React.createElement(NativeChartConfigComponent, { type: propElement.type, componentItem: {
                    component: nativeConfigItemsForCustomChart?.customsort
                        .component,
                    props: {
                        columnId: propElement.columnId,
                        orientation: propElement.orientation,
                        ...nativeConfigItemsForCustomChart?.customsort
                            .props,
                        isSortByMeasureEnabled: customSortElement.isSortByMeasureEnabled,
                    },
                }, onChangeHandler: onChange, elementKey: elementKey }));
        }
        default:
            return null;
    }
};
export const FormBuilderComponent = ({ visualPropDef, visualProps, onUpdateVisualProps, nativeConfigItemsForCustomChart, }) => {
    const handleChange = (key, value) => {
        const updatedProps = _.cloneDeep(visualProps);
        _.set(updatedProps, key, value);
        onUpdateVisualProps(updatedProps);
    };
    const { sections, nonSections, hasTabs } = useMemo(() => {
        const { elements } = visualPropDef || { elements: [] };
        const sections = elements.filter(element => element.type === 'section');
        const nonSections = elements.filter(element => element.type !== 'section');
        const layoutTypes = _.uniq(_.map(sections, 'layoutType'));
        if (layoutTypes.length > 1) {
            throw new Error('Top level Sections cannot have mixed layouts (tabs and accordions).');
        }
        const hasTabs = layoutTypes[0] === 'tab';
        return {
            sections,
            nonSections,
            hasTabs,
        };
    }, [visualPropDef?.elements]);
    return (React.createElement(React.Fragment, null, visualPropDef?.elements?.length > 0 ? (React.createElement("div", { "data-testid": "custom-chart-form-builder" },
        nonSections.map(elem => {
            return (React.createElement(PropElementComponent, { propElement: elem, visualProps: visualProps, elementKey: elem.key, key: elem.key, onChange: handleChange, nativeConfigItemsForCustomChart: nativeConfigItemsForCustomChart }));
        }),
        React.createElement(WrapWithOrWithoutTabs, { isTabLayout: hasTabs, onChange: handleChange, visualProps: visualProps, propElements: sections, nativeConfigItemsForCustomChart: nativeConfigItemsForCustomChart }))) : null));
};
export const MemoizedPropElementComponent = React.memo(PropElementComponent);
//# sourceMappingURL=form-builder.component.js.map